import { computed, watch, type ComputedRef, type Ref } from "vue";
import { assertNever } from "../lib/assertNever";
import { just, type Maybe } from "../lib/maybe";
import { useTea } from "../lib/tea";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import {
  initialSynopsisDraftModel,
  updateSynopsisDraftFn,
  type SynopsisDraftCmd,
} from "./useSynopsisDraft.update";

// Matches the standard editor autosave delay (`useEditorSaveStore`'s
// `autosaveDelayMs`) — every debounced save in the app commits on the same
// rhythm, with no blur-triggered early flush anywhere.
const SYNOPSIS_DEBOUNCE_MS = 1000;

export type SynopsisDraftContext = {
  draft: Ref<string>;
};

/**
 * Debounced-draft machinery shared by every synopsis field in the app
 * (`SceneTitleBar.vue`'s chapter/scene fields, and every Storyboard card).
 *
 * TEA: pure Model/Msg in `useSynopsisDraft.update.ts`.
 */
export function useSynopsisDraft(options: {
  identity: ComputedRef<Maybe<string>>;
  sourceValue: () => string;
  commit: (identity: string, value: string) => void | Promise<void>;
  debounceMs?: number;
}): SynopsisDraftContext {
  const debounceMs = options.debounceMs ?? SYNOPSIS_DEBOUNCE_MS;
  const saveTimer = createTimeoutHandle();

  function runCmd(cmd: SynopsisDraftCmd): void {
    switch (cmd.kind) {
      case "scheduleSave":
        saveTimer.schedule(debounceMs, () => {
          void dispatch({
            type: "SaveTimerFired",
            identity: cmd.identity,
            value: cmd.value,
          });
        });
        return;
      case "clearSave":
        saveTimer.clear();
        return;
      case "commit":
        void options.commit(cmd.identity, cmd.value);
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea(
    initialSynopsisDraftModel,
    updateSynopsisDraftFn,
    { runCmd: just(runCmd) },
  );

  watch(
    [options.identity, options.sourceValue],
    ([idM, val]) => {
      void dispatch({
        type: "IdentityOrSourceChanged",
        identity: idM,
        sourceValue: val,
      });
    },
    { immediate: true },
  );

  const draft = computed({
    get: (): string => model.value.draft,
    set: (value: string): void => {
      void dispatch({ type: "DraftChanged", draft: value });
    },
  });

  return { draft };
}
